import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { confirmAlert } from 'react-confirm-alert';
import {
  changeMallBannerOrder,
  deleteMallBanner,
  getMallList,
} from 'app/api/mall';
import { APP_CONSTANTS } from 'app/constants/app-constants';
import Message from 'app/utils/Message';
import Toast from 'app/utils/Toast';

const useMallBanner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [pageLoading, setPageLoading] = useState(false);
  const [mallList, setMallList] = useState([]);
  const [bannerList, setBannerList] = useState([]);
  const [selectedMallId, setSelectedMallId] = useState(
    location?.state?.mall_id || ''
  );

  // Keep the selection in step when we're navigated back here with a mall in
  // route state (e.g. returning from the create/edit form).
  useEffect(() => {
    if (location?.state?.mall_id) {
      setSelectedMallId(location.state.mall_id);
    }
  }, [location.state]);

  // Depends on mallList because loadBannerList reads the banners out of the
  // already-fetched mall list rather than refetching — on mount that list is
  // still empty, so without this the banners never load when arriving here
  // with a mall already selected.
  useEffect(() => {
    if (selectedMallId && mallList.length) {
      loadBannerList(selectedMallId);
    } else if (!selectedMallId) {
      setBannerList([]);
    }
  }, [selectedMallId, mallList]);

  useEffect(() => {
    getMallListforDropdown();
  }, []);

  const getMallListforDropdown = async () => {
    setPageLoading(true);
    try {
      const response = await getMallList(1); // Adjust parameters as needed
      setPageLoading(false);
      if (response?.responseCode === 200) {
        const temp = response.data.malls.map((item) => ({
          ...item,
          value: item.id,
          label: item.name,
        }));
        setMallList(temp);
      } else {
        Toast.error(
          response?.response?.message
            ? response?.response?.message
            : Message.Error.COMMON_MESSAGE
        );
      }
    } catch (error) {
      console.error('Error fetching mall list:', error);
      Toast.error(Message.Error.COMMON_MESSAGE);
      setPageLoading(false);
    }
  };

  const loadBannerList = async (mallId) => {
    setPageLoading(true);
    try {
      // Fetch banner list based on mallId
      const selectedMall = mallList.find((item) => item.id === mallId);
      setBannerList(selectedMall?.banner || []);
    } catch (error) {
      console.error('Error loading banner list:', error);
      Toast.error('Failed to load banner list');
    } finally {
      setPageLoading(false);
    }
  };

  const bannerOrder = async (tempData) => {
    const ServiceData = tempData.map((data, index) => ({
      id: data.original.id,
      sequence: index + 1,
    }));
    setPageLoading(true);
    try {
      const response = await changeMallBannerOrder(ServiceData);
      if (response?.responseCode === 200) {
        // DraggableTable resyncs its rows from this state on every render, so
        // the new order has to be committed here or the drop visually reverts.
        // Only the visible slice is draggable — keep the untouched tail.
        const reordered = tempData.map((row) => row.original);
        setBannerList((prev) => [...reordered, ...prev.slice(reordered.length)]);
        // loadBannerList reads banners back out of mallList, so keep the cached
        // mall entry in sync or re-selecting the mall resurrects the old order.
        setMallList((prev) =>
          prev.map((mall) =>
            mall.id === selectedMallId
              ? {
                  ...mall,
                  banner: [
                    ...reordered,
                    ...(mall.banner || []).slice(reordered.length),
                  ],
                }
              : mall
          )
        );
        Toast.success('Banner order updated successfully');
      } else {
        Toast.error(
          response?.response?.message
            ? response?.response?.message
            : 'Failed to update banner order'
        );
      }
    } catch (error) {
      console.error('Error updating banner order:', error);
      Toast.error('Failed to update banner order');
    } finally {
      setPageLoading(false);
    }
  };

  const callServiceDeleteBanner = async (bannerId) => {
    setPageLoading(true);
    try {
      const response = await deleteMallBanner(bannerId); // Adjust parameters as needed
      if (response?.responseCode === 200) {
        Toast.success('Banner deleted successfully');
        setBannerList((prevList) =>
          prevList.filter((banner) => banner.id !== bannerId)
        ); // Update local banner list
      } else {
        Toast.error(
          response?.response?.message
            ? response?.response?.message
            : 'Failed to delete banner'
        );
      }
    } catch (error) {
      console.error('Error deleting banner:', error);
      Toast.error('Failed to delete banner');
    } finally {
      setPageLoading(false);
    }
  };

  const handleDelete = (bannerId) => {
    confirmAlert({
      title: '',
      message: 'Are you sure you want to delete this banner?',
      buttons: [
        {
          label: 'Yes',
          onClick: () => callServiceDeleteBanner(bannerId.id),
        },
        {
          label: 'No',
        },
      ],
      overlayClassName: 'overlay-custom-class-name',
    });
  };

  const handleSelect = async (e) => {
    if (e && e.target) {
      // The effect above reloads the list off the back of this.
      setSelectedMallId(e.target.value);
    }
  };

  // BL-10/D-01: opens the dedicated per-banner analytics page, staying
  // inside the su-only banner area. mall_id in route state lets the page's
  // back button (and a future re-select) return to the same mall.
  const analyticsAction = (banner) => {
    navigate(
      APP_CONSTANTS.BANNER_ANALYTICS.replace(':id', banner.id),
      { state: { mall_id: selectedMallId } }
    );
  };

  return {
    handleDelete,
    mallList,
    pageLoading,
    handleSelect,
    bannerList,
    bannerOrder,
    navigate,
    mallId: selectedMallId,
    analyticsAction,
  };
};

export default useMallBanner;
